import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Search, Sparkles, RefreshCw } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { RichPlanCard } from '../components/PlanCard';

const CHIPS = [
  { key: '', label: 'For you' },
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'weekend', label: 'This Weekend' },
  { key: 'free', label: 'Free' },
  { key: 'nearby', label: 'Nearby' },
];

const CATEGORIES = [
  { key: 'coffee', label: 'Coffee' },
  { key: 'social', label: 'Social' },
  { key: 'wellness', label: 'Wellness' },
  { key: 'outdoors', label: 'Outdoors' },
  { key: 'food', label: 'Food' },
  { key: 'fitness', label: 'Fitness' },
  { key: 'art', label: 'Art' },
];

const PRICE_CHIPS = [
  { key: 'free', label: 'Free', cost: 'free' },
  { key: 'premium', label: 'Premium $$$', cost: 'premium' },
];

const PAGE_SIZE = 12;

// Discovery sections live under a different backend route (and don't share
// the legacy /plans/section chip filters). When the section key matches one
// of these, we hit the personalised endpoint instead.
const DISCOVERY_SECTIONS = new Set(['for_you', 'tonight', 'friends_going', 'interests', 'trending', 'surprise']);

export default function SectionFeed() {
  const { section } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [plans, setPlans] = useState([]);
  const [title, setTitle] = useState('Feed');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);

  const [chip, setChip] = useState(searchParams.get('chip') || '');
  const [category, setCategory] = useState(searchParams.get('category') || '');
  const [cost, setCost] = useState(searchParams.get('cost') || '');
  const [coords, setCoords] = useState(null);

  const sentinelRef = useRef(null);
  const scrollerRef = useRef(null);
  const pullStartY = useRef(null);
  const [pullOffset, setPullOffset] = useState(0);
  // City precedence: ?city= URL param (passed from Home See-all and persists on
  // refresh) > user's profile city > sensible default. This preserves the
  // location context the user picked on the Home feed when they navigate here.
  const city = searchParams.get('city') || user?.city || 'Abu Dhabi';

  // Geo: fetch user location once when nearby is selected
  useEffect(() => {
    if ((chip === 'nearby' || section === 'nearby') && !coords && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => setCoords(null),
        { timeout: 4000 }
      );
    }
  }, [chip, section, coords]);

  const fetchPage = useCallback(async (pageOffset, replace = false) => {
    const params = new URLSearchParams();
    params.set('city', city);
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String(pageOffset));
    const isDiscovery = DISCOVERY_SECTIONS.has(section);
    if (!isDiscovery) {
      if (chip) params.set('chip', chip);
      if (category) params.set('category', category);
      if (cost) params.set('cost', cost);
      if (coords) {
        params.set('lat', String(coords.lat));
        params.set('lng', String(coords.lng));
      }
    }
    const path = isDiscovery
      ? `/discovery/section/${section}?${params.toString()}`
      : `/plans/section/${section}?${params.toString()}`;
    const { data } = await api.get(path);
    setTitle(data.title || 'Feed');
    setHasMore(!!data.has_more);
    setPlans(prev => replace ? data.plans : [...prev, ...data.plans]);
    setOffset(pageOffset + PAGE_SIZE);
  }, [section, city, chip, category, cost, coords]);

  // Initial + filter changes — replace list
  useEffect(() => {
    let alive = true;
    const run = async () => {
      setLoading(true);
      try {
        await fetchPage(0, true);
      } catch (e) {
        if (alive) {
          setPlans([]);
          setHasMore(false);
        }
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [fetchPage]);

  // Infinite scroll
  useEffect(() => {
    if (!sentinelRef.current || loading || !hasMore) return;
    const observer = new IntersectionObserver(async (entries) => {
      if (entries[0].isIntersecting && !loadingMore && hasMore) {
        setLoadingMore(true);
        try {
          await fetchPage(offset, false);
        } catch (e) {
          // swallow
        } finally {
          setLoadingMore(false);
        }
      }
    }, { rootMargin: '600px 0px' });
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
  }, [fetchPage, offset, loading, loadingMore, hasMore]);

  // Pull to refresh
  const onTouchStart = (e) => {
    if (window.scrollY <= 0) {
      pullStartY.current = e.touches[0].clientY;
    } else {
      pullStartY.current = null;
    }
  };
  const onTouchMove = (e) => {
    if (pullStartY.current === null) return;
    const dy = e.touches[0].clientY - pullStartY.current;
    if (dy > 0) {
      setPullOffset(Math.min(dy * 0.45, 90));
    }
  };
  const onTouchEnd = async () => {
    if (pullStartY.current !== null && pullOffset > 60) {
      setRefreshing(true);
      try {
        await fetchPage(0, true);
      } catch {}
      setRefreshing(false);
    }
    pullStartY.current = null;
    setPullOffset(0);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try { await fetchPage(0, true); } catch {}
    setRefreshing(false);
  };

  const handleDismiss = (planId) => {
    setPlans(prev => prev.filter(p => p.id !== planId));
  };

  const isDiscovery = DISCOVERY_SECTIONS.has(section);

  return (
    <div
      ref={scrollerRef}
      data-testid="section-feed-page"
      className="min-h-[100dvh] bg-[#F7F6F2] pb-32"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Header */}
      <div
        className="sticky top-0 z-40 bg-[#F7F6F2]/95 backdrop-blur-xl border-b border-black/[0.05]"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="flex items-center gap-2 px-3 pt-3 pb-2">
          <button
            data-testid="section-feed-back"
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-white border border-black/[0.06] flex items-center justify-center active:scale-90 transition-transform"
          >
            <ArrowLeft size={18} className="text-[#111111]" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-[17px] font-extrabold text-[#111111] tracking-tight truncate">{title}</h1>
            <p className="text-[11px] text-[#9B9EA3] -mt-0.5">{city}{plans.length > 0 ? ` · ${plans.length}+ plans` : ''}</p>
          </div>
          <button
            data-testid="section-feed-refresh"
            onClick={handleRefresh}
            className="w-10 h-10 rounded-full bg-white border border-black/[0.06] flex items-center justify-center active:scale-90 transition-transform"
          >
            <RefreshCw size={16} className={`text-[#111111] ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Chips — hidden for personalised Discovery sections since their
            content is already curated and the chips would change the source
            of truth. */}
        {!isDiscovery && (
        <div className="px-3 pb-3 flex gap-2 overflow-x-auto hide-scrollbar" data-testid="section-feed-chips">
          {CHIPS.map(c => (
            <button
              key={c.key || 'foryou'}
              data-testid={`chip-${c.key || 'foryou'}`}
              onClick={() => { setChip(c.key); setCategory(''); setCost(''); }}
              className={`feed-chip ${chip === c.key && !category && !cost ? 'feed-chip-active' : ''}`}
            >
              {c.label}
            </button>
          ))}
          <span className="w-px self-stretch bg-black/[0.08] mx-1 my-1" />
          {CATEGORIES.map(c => (
            <button
              key={c.key}
              data-testid={`chip-cat-${c.key}`}
              onClick={() => { setCategory(category === c.key ? '' : c.key); setChip(''); }}
              className={`feed-chip ${category === c.key ? 'feed-chip-active' : ''}`}
            >
              {c.label}
            </button>
          ))}
          <span className="w-px self-stretch bg-black/[0.08] mx-1 my-1" />
          {PRICE_CHIPS.map(c => (
            <button
              key={c.key}
              data-testid={`chip-price-${c.key}`}
              onClick={() => { setCost(cost === c.cost ? '' : c.cost); setChip(''); }}
              className={`feed-chip ${cost === c.cost ? 'feed-chip-active' : ''}`}
            >
              {c.label}
            </button>
          ))}
        </div>
        )}
      </div>

      {/* Pull to refresh indicator */}
      {pullOffset > 0 && (
        <div className="flex justify-center" style={{ height: pullOffset }} data-testid="pull-to-refresh">
          <RefreshCw
            size={20}
            className={`text-[#6B8A00] mt-3 transition-transform ${pullOffset > 60 ? 'animate-spin' : ''}`}
            style={{ transform: `rotate(${pullOffset * 3}deg)` }}
          />
        </div>
      )}

      {/* Body */}
      <div className="px-4 pt-5 space-y-5" data-testid="section-feed-list">
        {loading && (
          <div className="space-y-5" data-testid="feed-loading">
            {[1, 2, 3].map(i => (
              <div key={i} className="rounded-[28px] overflow-hidden border border-black/[0.06] bg-white">
                <div className="skeleton h-[280px] w-full !rounded-none" />
                <div className="p-5 space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="skeleton w-10 h-10 !rounded-full" />
                    <div className="flex-1 space-y-2">
                      <div className="skeleton h-3 w-32" />
                      <div className="skeleton h-2.5 w-20" />
                    </div>
                  </div>
                  <div className="skeleton h-3 w-3/4" />
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && plans.length === 0 && (
          <div className="py-16 px-4 text-center animate-fade-in" data-testid="feed-empty">
            <div className="w-16 h-16 rounded-full bg-[#B6E600]/15 flex items-center justify-center mx-auto mb-4 animate-float">
              <Sparkles size={26} className="text-[#6B8A00]" />
            </div>
            <h3 className="text-[18px] font-extrabold text-[#111111] mb-1.5 tracking-tight">No plans match this yet</h3>
            <p className="text-[13px] text-[#6B6E73] mb-6 leading-relaxed">
              Try a different filter, or be the first to host something for {title.toLowerCase()}.
            </p>
            <div className="flex gap-2 justify-center">
              <button
                data-testid="feed-empty-clear"
                onClick={() => { setChip(''); setCategory(''); setCost(''); }}
                className="px-4 py-2 rounded-full text-[13px] font-semibold bg-white border border-black/[0.08] text-[#111111] active:scale-95 transition-transform"
              >
                Clear filters
              </button>
              <button
                data-testid="feed-empty-create"
                onClick={() => navigate('/create')}
                className="px-4 py-2 rounded-full text-[13px] font-bold bg-[#B6E600] text-[#111111] active:scale-95 transition-transform"
              >
                Host a plan
              </button>
            </div>
          </div>
        )}

        {!loading && plans.map((p, i) => (
          <RichPlanCard key={p.id} plan={p} index={i} onDismiss={handleDismiss} />
        ))}

        {/* Infinite-scroll sentinel */}
        {!loading && hasMore && (
          <div ref={sentinelRef} className="py-6 flex justify-center" data-testid="feed-sentinel">
            <RefreshCw size={18} className="text-[#9B9EA3] animate-spin" />
          </div>
        )}
        {!loading && !hasMore && plans.length > 0 && (
          <div className="py-6 text-center text-[11px] text-[#9B9EA3]" data-testid="feed-end">
            You're all caught up
          </div>
        )}
      </div>
    </div>
  );
}
